import React, { useState, useEffect } from "react";
import { FormikProps } from "formik";
import { Form } from "react-bootstrap";

interface AmountFieldProps {
  name: string;
  formik?: FormikProps<any>;
  label: string;
  placeholder: string;
  icon?: string;
}

const AmountField = ({
  name,
  formik,
  label,
  placeholder,
  icon,
  value,
  onChange,
  onBlur,
  ...rest
}: AmountFieldProps & React.InputHTMLAttributes<HTMLInputElement>) => {
  const [inputValue, setInputValue] = useState<string>(
    formik?.values?.[name] != null
      ? `${formik.values[name]}`
      : value != null
        ? `${value}`
        : ""
  );

  const formikError =
    formik?.touched?.[name] && formik?.errors?.[name]
      ? formik.errors[name]
      : null;

  /* ----------------------------------
     Sync external value → local state
  ---------------------------------- */
  useEffect(() => {
    if (formik) {
      setInputValue(
        formik?.values?.[name] != null ? `${formik.values[name]}` : ""
      );
    } else {
      setInputValue(value != null ? `${value}` : "");
    }
  }, [formik?.values?.[name], value]);

  /* ----------------------------------
     CHANGE HANDLER
     - Restriction ONLY for non-formik
  ---------------------------------- */
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let val = event.target.value;

    // Always sanitize basic numeric input
    val = val.replace(/[^0-9.]/g, "");

    if (val.startsWith(".")) {
      val = "0" + val;
    }

    // 🚫 Apply restriction ONLY when NOT using Formik
    if (!formik) {
      const [integerPart, decimalPart] = val.split(".");

      // Limit integer part to 4 digits
      const limitedInteger = integerPart.slice(0, 4);

      // Limit decimal part to 2 digits
      val =
        decimalPart !== undefined
          ? `${limitedInteger}.${decimalPart.slice(0, 2)}`
          : limitedInteger;
    }

    setInputValue(val);

    // Update Formik (NO restriction)
    if (formik) {
      formik.setFieldValue(name, val);
    }

    // Bubble sanitized value for non-formik usage
    if (onChange) {
      onChange({
        ...event,
        target: {
          ...event.target,
          value: val,
          name,
        },
      } as any);
    }
  };

  /* ----------------------------------
     BLUR HANDLER
     - Format decimals (safe for both)
  ---------------------------------- */
  const handleBlur = () => {
    let val = inputValue;

    if (!val) return;

    if (!val.includes(".")) {
      val += ".00";
    } else {
      const parts = val.split(".");
      if (parts[1].length === 0) val += "00";
      if (parts[1].length === 1) val += "0";
    }

    setInputValue(val);

    if (formik) {
      formik.setFieldValue(name, val);
    }

    if (onBlur) {
      onBlur({
        target: {
          name,
          value: val,
        },
      } as any);
    }
  };

  return (
    <Form.Group className={`field_wrap ${rest.className || ""}`} controlId={name}>
      {label && <Form.Label>{label}</Form.Label>}

      <div className="input_wrap amount_input">
        {icon && <img className="field_icon" src={icon} alt="icon" />}

        <Form.Control
          type="text"
          name={name}
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleBlur}
          className={formikError ? "has_error" : ""}
        />
      </div>

      {formikError && (
        <span className="error">
          {typeof formikError === "string" ? formikError : ""}
        </span>
      )}
    </Form.Group>
  );
};

export default AmountField;
